<script setup lang="ts">
  import { defineAsyncComponent, ref, onMounted, watch, provide, computed, onBeforeUnmount } from 'vue';
  import { useRoute } from 'vue-router';
  import { storeToRefs } from 'pinia';
  import { ResponseMenu, Category, AllergyTag, Product } from '../models/responseMenu.interface';
  import { useMenuStore } from "@/store";
  import { parentListCardKey } from '../helpers/injectionKeys';
  import { useClickEvent } from '@/helpers/composables';
  import { useScrollCategories } from '@/helpers/scrollCategories-composables';

  interface OrganismHomeContentChildMethods {
    categorieData: (categorie:Category) => void;
    flatSubCategoriesFalse: () => void;
  };

  import OrganismHomeContent from '@/components/organisms/OrganismHomeContent.vue';
  import LayoutRateAndSettingsMenu from '@/components/layouts/LayoutRateAndSettingsMenu.vue';
  import CategoriesPager from '@/components/categories-pager/CategoriesPager.vue';
  const ModalAllergens = defineAsyncComponent(() => import("@/components/modal/ModalAllergens.vue"));

  const menuStore = useMenuStore();
  const route = useRoute();
  const {
    menuData,
    skeletonLoad,
    allergenPopup,
    flatButtonRecommendedCategoriePosition,
    flagCssCustomObj,
    customerData,
    currentCategory,
    countAlertAlegents,
    isMenuPdfView
  } = storeToRefs(menuStore);
  const { findCategory,clickCategory } = useClickEvent();
  const {
    handleScroll,
    detectScrollDirection,
    mainCategories,
    leftButtonPreviusCategorie,
    rightButtonNextCategorie,
    divElementId,flatNearDivLimit,flatButtonDiv,scrollPosition
  } = useScrollCategories();

  let listAllergenTags = ref<AllergyTag[]>([]);
  let currentSlide = ref(0);
  let startX = ref<number>(0);
  let endX = ref<number>(0);
  let startY = ref<number>(0);
  let endY = ref<number>(0);
  let listProductsCategories = ref<Product[]>([]);
  const FRAME_DELAY = 30;
  let openModalAllergens = ref<boolean>(false);

  const OrganismHomeContentChild = ref<null | (OrganismHomeContentChildMethods & InstanceType<typeof OrganismHomeContent>)>(null);

  provide({parentListCardKey}, 'home');
  provide('listProductsCategories', listProductsCategories);
  provide('openModalAllergensKey', openModalAllergens);
  provide('setIsModalOpen', (value: boolean) => {
    openModalAllergens.value = value;
  });


  const routeCategory = computed(() => route.params?.category_id || route.params?.subcategory_id);

  watch(menuData, (newMenuData) => {
    setMenuData(newMenuData);
  });
  // CAMBIAR LA COMPUTED DE ROUTE Y EL WATCH A EL ORGANISMO ORGANISMHOMECONTENT Y DESDE ALLA MANEJAR LA VISUAL DE LAS CATEGORIAS
  // DESDE EL ORGANISMO HOMECONTENT ENVIAR EL INDEX DE LA CATEGORIA A LA VISTA PARA MANEJAR EL SLIDE Y ACTIVECLASS DE LA CATEGORIA ENRUTADA
  watch(routeCategory, (newVal) => {
    if (newVal) {
      const category = findCategory(menuData.value.menu.categories, Number(newVal));
      if (category) {
        document.title = category.label || '';
        triggerChildCategoryData(category); // SOLUCIÓN TEMPORAL PARA EL ENRUTAMIENTO DE LAS CATEGORIAS DESDE BANNER Y POPUP
      };
    } else {
      if (flatButtonRecommendedCategoriePosition.value) goToRecommendMenuLateral();
      listProductsCategories.value = [];
      OrganismHomeContentChild.value?.flatSubCategoriesFalse();
    };
  });

  const goToRecommendMenuLateral = () => {
    if (menuData.value.content_home.length) {
      const categorieRecommend = findCategory(mainCategories.value,1);
      const categorieRecommendIndex = mainCategories.value.indexOf(categorieRecommend as Category);
      slideTo(categorieRecommendIndex);
      flatButtonRecommendedCategoriePosition.value = false;
    };
  };
  const setMenuData = (menu: ResponseMenu) => {
    countAlertAlegents.value++;
    if (Object.keys(menu).length){
      skeletonLoad.value = false;
      if (countAlertAlegents.value === 1) hasAlergensTags(menu);
    };
  };
  const hasAlergensTags = (menu: ResponseMenu) => {
    const allergensTags = menu?.menu?.products.filter(prod => prod.allergy_tags.length > 0).flatMap(prodAllergen => prodAllergen.allergy_tags.map(allergen => allergen))||[];
    listAllergenTags.value = allergensTags.filter((item, index, self) => index === self.findIndex((d) => d.name === item.name && d.css_class === item.css_class));
    if (flagCssCustomObj.value.flagAllergenPopup && listAllergenTags.value.length > 0) {
      allergenPopup.value.hasAllergens = true;
      allergenPopup.value.show = true;
    };
  };
  const leftButton = () => {
    let previusCategorie = leftButtonPreviusCategorie();
    slideTo(previusCategorie.previusIndex);
    triggerChildCategoryData(previusCategorie.previusCategory)
    clickCategory(previusCategorie.previusCategory);
  };
  const rightButton = () => {
    let nextcategorie = rightButtonNextCategorie();
    slideTo(nextcategorie.nextIndex);
    triggerChildCategoryData(nextcategorie.nextCategory);
    clickCategory(nextcategorie.nextCategory);
  };
  const slideTo = (val: number) => {
    currentSlide.value = val;
    if (!route.params.subcategory_id) setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 1000);
  };
  const handleTouchStart = (event:any) => {
    startX.value = event.touches[0].clientX;
    startY.value = event.touches[0].clientY;
  };
  const handleTouchEnd = (event:any) => {
    endX.value = event.changedTouches[0].clientX;
    endY.value = event.changedTouches[0].clientY;
    // detectSwipeDirection();
  };
  // const detectSwipeDirection = () => {
  //   const deltaX = endX.value - startX.value;
  //   const deltaY = endY.value - startY.value;
  //   if (Math.abs(deltaX) > Math.abs(deltaY)) {
  //     if (deltaX > 0)  delayedSlide(leftButton);
  //     else if (deltaX < 0) delayedSlide(rightButton);
  //     else ('Sin swipe horizontal');
  //   } else {
  //     if (deltaY > 0) ('Swipe hacia abajo');
  //     else if (deltaY < 0) ('Swipe hacia arriba');
  //     else ('Sin swipe vertical');
  //   };
  // };
  const triggerChildCategoryData = (categorie:Category) => {
    OrganismHomeContentChild.value?.categorieData(categorie);
  };
  const delayedSlide = (slideFunction: () => void) => {
    let frameCount = 0;
    const frameHandler = () => {
      frameCount++;
      if (frameCount >= FRAME_DELAY) slideFunction();
      else requestAnimationFrame(frameHandler);
    };
    requestAnimationFrame(frameHandler);
  };

  onMounted(() => {
    setMenuData(menuData.value);
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", detectScrollDirection);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
    window.addEventListener("scroll", detectScrollDirection);
  });
</script>

<template>
  <OrganismHomeContent
    ref="OrganismHomeContentChild"
    :currentSlide="currentSlide"  :divElementId="divElementId as string" :flatNearDivLimit="flatNearDivLimit" :flatButtonDiv="flatButtonDiv" :scrollPosition="scrollPosition"
    @slideTo="slideTo" @handleTouchStart="handleTouchStart" @handleTouchEnd="handleTouchEnd"
  />
  <LayoutRateAndSettingsMenu v-if="flagCssCustomObj.flagButtonMenuLat" :menuData="menuData" :flagCssCustomObj="flagCssCustomObj" />
  <CategoriesPager
    v-if="flagCssCustomObj.flagCategoriesPager"
    :currentSlide="currentSlide" :mainCategories="mainCategories" :currentCategory="currentCategory" :customerData="customerData"
    @leftButton="leftButton" @rightButton="rightButton" @slideTo="slideTo" @categorieData="triggerChildCategoryData"
  />
  <ModalAllergens v-if="openModalAllergens && !isMenuPdfView" :allergens="listAllergenTags" />
</template>

<style scoped lang="scss">
  .smooth-slide {
    transition: transform 0.7s ease;
  }
</style>